import { useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function SelectionImageCarousel({ eyebrow, intro, items, ariaLabel, className = '' }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [itemStep, setItemStep] = useState(382);
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef(null);
  const dragState = useRef({ active: false, startX: 0, offset: 0 });

  const moveCarousel = (direction) => {
    setActiveIndex((currentIndex) => Math.min(Math.max(currentIndex + direction, 0), items.length - 1));
  };

  useLayoutEffect(() => {
    const updateItemStep = () => {
      const track = trackRef.current;
      const firstCard = track?.querySelector('.selection-carousel-card');
      if (!track || !firstCard) return;

      const gap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0');
      setItemStep(firstCard.getBoundingClientRect().width + gap);
    };

    updateItemStep();
    window.addEventListener('resize', updateItemStep);
    return () => window.removeEventListener('resize', updateItemStep);
  }, []);

  const startDrag = (event) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragState.current = { active: true, startX: event.clientX, offset: 0 };
    setIsDragging(true);
    setDragOffset(0);
  };

  const moveDrag = (event) => {
    if (!dragState.current.active) return;
    event.preventDefault();

    const nextOffset = event.clientX - dragState.current.startX;
    const atFirst = activeIndex === 0 && nextOffset > 0;
    const atLast = activeIndex === items.length - 1 && nextOffset < 0;
    const adjustedOffset = atFirst || atLast ? nextOffset * 0.25 : nextOffset;
    dragState.current.offset = adjustedOffset;
    setDragOffset(adjustedOffset);
  };

  const stopDrag = (event) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const threshold = Math.min(itemStep * 0.25, 90);
    if (dragState.current.offset <= -threshold) {
      moveCarousel(1);
    } else if (dragState.current.offset >= threshold) {
      moveCarousel(-1);
    }

    dragState.current = { active: false, startX: 0, offset: 0 };
    setIsDragging(false);
    setDragOffset(0);
  };

  return (
    <section className={`selection-carousel-section${className ? ` ${className}` : ''}`}>
      {eyebrow || intro ? (
        <div className="cart-carousel-head">
          <div>
            {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
            {intro ? <div className="body-copy">{intro}</div> : null}
          </div>
        </div>
      ) : null}
      <div
        className={`selection-carousel-window${isDragging ? ' dragging' : ''}`}
        aria-label={ariaLabel}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
      >
        <div
          ref={trackRef}
          className="selection-carousel-track"
          style={{ transform: `translateX(${activeIndex * -itemStep + dragOffset}px)` }}
        >
          {items.map((item) => (
            <figure className="cart-option-card selection-carousel-card" key={item.src}>
              <div className="cart-option-image selection-carousel-image">
                <img loading="lazy" decoding="async" src={item.src} alt={item.alt} draggable="false" />
              </div>
              {item.caption ? <figcaption>{item.caption}</figcaption> : null}
            </figure>
          ))}
        </div>
      </div>
      <div className="cart-carousel-controls" aria-label={`${ariaLabel} controls`}>
        <button type="button" onClick={() => moveCarousel(-1)} aria-label="Show previous image">
          <ChevronLeft size={22} />
        </button>
        <div className="carousel-dots" aria-hidden="true">
          {items.map((item, index) => (
            <span key={item.src} className={index === activeIndex ? 'active' : ''}></span>
          ))}
        </div>
        <button type="button" onClick={() => moveCarousel(1)} aria-label="Show next image">
          <ChevronRight size={22} />
        </button>
      </div>
    </section>
  );
}

export default SelectionImageCarousel;
